// Volumes: oriented boxes in the scene frame that hide points, mark points for
// alignment, or mark known solid surfaces. Same maths as prep/volumes.py.
import { type V3, add, scale } from './math.ts';

export type Kind = 'hide' | 'align' | 'solid';
export const KINDS: Kind[] = ['hide', 'align', 'solid'];
export const KIND_COLOR: Record<Kind, [number, number, number]> = { hide: [1, 0.3, 0.25], align: [0.2, 0.85, 1], solid: [1, 0.72, 0.1] };
export const MAX_VOLUMES = 32;

export interface Volume {
  id: string;
  name: string;
  kind: Kind;
  on: boolean;
  center: V3;
  size: V3;          // full edge lengths along the box's own x, y, z
  ypr: V3;           // yaw (about Z), pitch (about Y), roll (about X), degrees: R = Rz(yaw) Ry(pitch) Rx(roll)
  clouds: string[];  // cloud ids it applies to; empty = all
}

const rad = (d: number) => (d * Math.PI) / 180;

/** Rotation matrix rows (row-major 3x3); columns are the box axes in the world. */
export function rotation(ypr: V3): [V3, V3, V3] {
  const [cy, sy, cp, sp, cr, sr] = [Math.cos(rad(ypr[0])), Math.sin(rad(ypr[0])), Math.cos(rad(ypr[1])), Math.sin(rad(ypr[1])), Math.cos(rad(ypr[2])), Math.sin(rad(ypr[2]))];
  return [
    [cy * cp, cy * sp * sr - sy * cr, cy * sp * cr + sy * sr],
    [sy * cp, sy * sp * sr + cy * cr, sy * sp * cr - cy * sr],
    [-sp, cp * sr, cp * cr],
  ];
}

/** Box axis k (0..2) as a world unit vector. */
export const axis = (R: [V3, V3, V3], k: number): V3 => [R[0][k], R[1][k], R[2][k]];

/** World point from box-local coordinates in [-1, 1]^3. */
export function boxPoint(v: Volume, u: V3, R = rotation(v.ypr)): V3 {
  let p = v.center;
  for (let k = 0; k < 3; k++) p = add(p, scale(axis(R, k), (u[k] * v.size[k]) / 2));
  return p;
}

/** Three affine rows mapping a world point to [-1, 1]^3 inside the box (12 floats). */
export function unitRows(v: Volume): number[] {
  const R = rotation(v.ypr), out: number[] = [];
  for (let k = 0; k < 3; k++) {
    const a = axis(R, k), s = 2 / Math.max(v.size[k], 1e-6);
    out.push(a[0] * s, a[1] * s, a[2] * s, -(a[0] * v.center[0] + a[1] * v.center[1] + a[2] * v.center[2]) * s);
  }
  return out;
}

/** Mask of packed points inside any of the boxes. */
export function inBoxes(xyz: Float32Array, boxes: Volume[]): Uint8Array {
  const rows = boxes.map(unitRows), n = xyz.length / 3, m = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const x = xyz[3 * i], y = xyz[3 * i + 1], z = xyz[3 * i + 2];
    for (const r of rows)
      if (Math.abs(r[0] * x + r[1] * y + r[2] * z + r[3]) <= 1 && Math.abs(r[4] * x + r[5] * y + r[6] * z + r[7]) <= 1 &&
          Math.abs(r[8] * x + r[9] * y + r[10] * z + r[11]) <= 1) { m[i] = 1; break; }
  }
  return m;
}

export function inside(v: Volume, p: V3): boolean {
  const r = unitRows(v);
  for (let k = 0; k < 3; k++) if (Math.abs(r[4 * k] * p[0] + r[4 * k + 1] * p[1] + r[4 * k + 2] * p[2] + r[4 * k + 3]) > 1) return false;
  return true;
}

/** A box's 12 edges as pairs of its corners, corner i at (i & 1 ? + : -, i & 2 ? + : -, i & 4 ? + : -) along its axes. */
export const BOX_EDGES = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [1, 3], [4, 6], [5, 7], [0, 4], [1, 5], [2, 6], [3, 7]] as const;

/** Line-segment vertices (24 points) for the box's 12 edges. */
export function edges(v: Volume): number[] {
  const R = rotation(v.ypr), c = (i: number) => boxPoint(v, [i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1], R);
  const e: number[] = [];
  for (const [i, j] of BOX_EDGES) e.push(...c(i), ...c(j));
  return e;
}

/**
 * What a point is, after all volumes: hidden (in an enabled hide box and in no
 * solid box), and whether it is in an align or a solid box. Mirrors the shader.
 */
export function classify(vols: Volume[], p: V3, cloud?: string) {
  let hide = false, align = false, solid = false;
  for (const v of vols) {
    if (!v.on || (cloud !== undefined && v.clouds.length && !v.clouds.includes(cloud)) || !inside(v, p)) continue;
    if (v.kind === 'hide') hide = true; else if (v.kind === 'align') align = true; else solid = true;
  }
  return { hidden: hide && !solid, align, solid };
}

// ── file format ─────────────────────────────────────────────────────────
export interface VolumeFile { version: 1; scene?: string; frame?: string; volumes: Volume[] }

const num3 = (a: unknown): V3 | null =>
  Array.isArray(a) && a.length === 3 && a.every(x => typeof x === 'number' && Number.isFinite(x)) ? [a[0], a[1], a[2]] : null;

/** Validate one volume from untrusted JSON; null if unusable. */
export function parseVolume(o: unknown, i = 0): Volume | null {
  if (!o || typeof o !== 'object') return null;
  const r = o as Record<string, unknown>;
  const center = num3(r.center), size = num3(r.size), ypr = num3(r.ypr ?? [0, 0, 0]);
  if (!center || !size || !ypr || size.some(s => s <= 0)) return null;
  const kind = KINDS.includes(r.kind as Kind) ? (r.kind as Kind) : null;
  if (!kind) return null;
  return {
    id: typeof r.id === 'string' && r.id ? r.id.slice(0, 40) : `v${i + 1}`,
    name: typeof r.name === 'string' ? r.name.slice(0, 80) : '',
    kind, on: r.on !== false, center, size, ypr,
    clouds: Array.isArray(r.clouds) ? r.clouds.filter((c): c is string => typeof c === 'string').slice(0, 64) : [],
  };
}

export function parseVolumes(json: unknown): Volume[] {
  const list = Array.isArray(json) ? json : (json as { volumes?: unknown })?.volumes;
  if (!Array.isArray(list)) throw new Error('no "volumes" list');
  const out: Volume[] = [], ids = new Set<string>();
  list.forEach((o, i) => {
    const v = parseVolume(o, i);
    if (!v || out.length >= MAX_VOLUMES) return;
    while (ids.has(v.id)) v.id += '_';
    ids.add(v.id);
    out.push(v);
  });
  return out;
}

export function toFile(vols: Volume[], scene?: string, frame?: string): VolumeFile {
  const r = (a: V3, d: number) => a.map(x => +x.toFixed(d)) as V3;
  return {
    version: 1, scene, frame,
    volumes: vols.map(v => ({ ...v, center: r(v.center, 4), size: r(v.size, 4), ypr: r(v.ypr, 3), clouds: [...v.clouds] })),
  };
}

export function nextId(vols: Volume[]): string {
  let n = vols.length + 1;
  while (vols.some(v => v.id === `v${n}`)) n++;
  return `v${n}`;
}
